import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CardFooterComponent } from '../card-footer/card-footer.component';
import { HeaderComponent } from '../header/header.component';
import { EmployeeAuthService, SignupRequest } from '../services/employee-auth.service';
import { EmployerAuthService } from '../services/employer-auth.service';
import { TermsAndConditions, TermsService } from '../services/terms.service';

@Component({
    selector: 'app-shared-agreement',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        HeaderComponent,
        CardFooterComponent,
        TranslateModule,
    ],
    templateUrl: './agreement.component.html',
    styleUrl: './agreement.component.css',
})
export class AgreementComponent implements OnInit {
    @Input() userType: 'employer' | 'employee' = 'employee';
    @Input() signupData: SignupRequest | null = null;

    termsAndConditionsAccepted = false;
    locationUsageAccepted = false;
    contactPermissionAccepted = false;
    showTermsModal = false;
    termsData: TermsAndConditions | null = null;
    isLoadingTerms = false;
    isLoading = false;
    showSuccessScreen = false;
    errorMessage = '';

    constructor(
        private titleService: Title,
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private termsService: TermsService,
        private employeeAuthService: EmployeeAuthService,
        private employerAuthService: EmployerAuthService
    ) { }

    ngOnInit(): void {
        this.translate.use(this.translate.currentLang || 'en');
        
        // Get userType from route data, fallback to current userType
        const routeUserType = this.route.snapshot.data['userType'] as 'employer' | 'employee';
        if (routeUserType) {
            this.userType = routeUserType;
        }
        
        // Always read signupData from sessionStorage
        const stored = sessionStorage.getItem('agreementSignupData');
        if (stored) {
            this.signupData = JSON.parse(stored);
        }
        if (!this.signupData) {
            this.router.navigate([this.userType === 'employer'
                ? '/employer/account/signup'
                : '/employee/account/signup']);
            return;
        }
        this.titleService.setTitle((this.userType === 'employer' ? 'Employer' : 'Employee') + ' | Agreement');
    }

    areAllCheckboxesChecked(): boolean {
        return this.termsAndConditionsAccepted &&
            this.locationUsageAccepted &&
            this.contactPermissionAccepted;
    }

    onContinue(): void {
        if (!this.areAllCheckboxesChecked() || this.isLoading) return;
        this.isLoading = true;
        this.errorMessage = '';
        if (this.userType === 'employee' && this.signupData) {
            this.employeeAuthService.signup(this.signupData).subscribe({
                next: (response: any) => {
                    if (response && response.data && response.data.sessionId) {
                        const sessionId = response.data.sessionId;
                        const sessionData = {
                            sessionToken: sessionId,
                            trxId: response.data.trxId,
                            email: this.signupData?.email,
                        };
                        sessionStorage.setItem('employeeSessionData', JSON.stringify(sessionData));
                        this.employeeAuthService.acceptTerms({
                            sessionId,
                            termsAccepted: true,
                            privacyAccepted: true,
                            locationAccepted: this.locationUsageAccepted,
                            contactAccepted: this.contactPermissionAccepted
                        }).subscribe({
                            next: (acceptRes: any) => {
                                this.isLoading = false;
                                if (acceptRes && acceptRes.success === true) {
                                    this.router.navigate(['/employee/account/verify'], { queryParams: { type: 'employee-signup' } });
                                } else {
                                    this.errorMessage = acceptRes?.message || 'Failed to send OTP. Please try again.';
                                }
                            },
                            error: (acceptErr) => {
                                this.isLoading = false;
                                this.errorMessage = acceptErr?.error?.message || 'Failed to accept terms.';
                            }
                        });
                    } else {
                        this.isLoading = false;
                        this.errorMessage = 'Signup failed. Please try again.';
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.errorMessage = error?.error?.message || 'Unknown error occurred.';
                }
            });
        } else if (this.userType === 'employer' && this.signupData) {
            this.employerAuthService.signup(this.signupData).subscribe({
                next: (response: any) => {
                    if (response && response.data && response.data.sessionId) {
                        const sessionId = response.data.sessionId;
                        const sessionData = {
                            sessionId: sessionId,
                            trxId: response.data.trxId,
                            email: this.signupData?.email,
                        };
                        sessionStorage.setItem('employerSessionData', JSON.stringify(sessionData));
                        this.employerAuthService.acceptTerms({
                            sessionId,
                            termsAccepted: true,
                            privacyAccepted: true,
                            locationAccepted: this.locationUsageAccepted,
                            contactAccepted: this.contactPermissionAccepted
                        }).subscribe({
                            next: (acceptRes: any) => {
                                this.isLoading = false;
                                if (acceptRes && acceptRes.success === true) {
                                    this.router.navigate(['/employer/account/verify'], { queryParams: { type: 'signup' } });
                                } else {
                                    this.errorMessage = acceptRes?.message || 'Failed to send OTP. Please try again.';
                                }
                            },
                            error: (acceptErr) => {
                                this.isLoading = false;
                                this.errorMessage = acceptErr?.error?.message || 'Failed to accept terms.';
                            }
                        });
                    } else {
                        this.isLoading = false;
                        this.errorMessage = 'Signup failed. Please try again.';
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.errorMessage = error?.error?.message || 'Unknown error occurred.';
                }
            });
        }
    }

    openTermsAndConditions(event: Event): void {
        event.preventDefault();
        this.showTermsModal = true;
        if (!this.termsData) {
            this.loadTermsAndConditions();
        }
    }

    loadTermsAndConditions(): void {
        this.isLoadingTerms = true;
        this.termsService.getTermsAndConditions().subscribe({
            next: (data) => {
                this.termsData = data;
                this.isLoadingTerms = false;
            },
            error: (error) => {
                this.isLoadingTerms = false;
            }
        });
    }

    closeTermsModal(): void {
        this.showTermsModal = false;
    }

    getSuccessTitleKey(): string {
        return this.userType === 'employer'
            ? 'EMPLOYER.AGREEMENT.SUCCESS_TITLE'
            : 'EMPLOYEE.AGREEMENT.SUCCESS_TITLE';
    }

    getSuccessRedirectMessageKey(): string {
        return this.userType === 'employer'
            ? 'EMPLOYER.AGREEMENT.SUCCESS_REDIRECT_MESSAGE'
            : 'EMPLOYEE.AGREEMENT.SUCCESS_REDIRECT_MESSAGE';
    }
} 